<template>
  <section id="rush-academy-video" v-if="detail">
    <div class="rounded overflow-hidden">
      <youtube
        :video-id="videoId"
        ref="youtube"
        :fitParent="true"
        :resize="true"
        :player-vars="{ rel: 0, start: computedInProgressStartTime }"
        @playing="handlePlaying(true)"
        @paused="handlePlaying(false)"
        @ended="handleVideoEnded">
      </youtube>
    </div>
    <div class="mt-4">
      <div class="d-flex justify-content-between align-items-start align-items-md-center mb-3 flex-column flex-md-row">
        <h2 class="mb-0">{{ detail.title }}</h2>
        <b-button
          id="play-next-button" class="bg-transparent border-0 shadow-none px-0 flex-shrink-0"
          @click="handlePlayNext(videoId)"
          v-if="videoId !== lastVideoId">
          <img src="@/assets/images/rush-academy/play-icon.svg" alt="Play" class="mr-2">
          Play Next Tutorial
        </b-button>
      </div>

      <p class="description">{{ detail.description }}</p>

      <div class="row">
        <div 
          v-if="hasPermissionTo(detail.submodule_code)"
          class="col-12 mb-2" 
        >
          <button
            id="try-button"
            class="bg-transparent text-primary border-0 shadow-none px-0"
            @click="handleRedirectUrl"
          >
            {{ detail.hyperlink_title }} <img src="@/assets/images/rush-academy/angle-right-solid.svg" alt="Try" class="ml-2">
          </button>
        </div>

        <div class="col-12 accordion">
          <button
            id="see-more-button"
            class="bg-transparent text-primary border-0 shadow-none px-0"
            v-b-toggle.see-more
          >
            See more <img src="@/assets/images/rush-academy/angle-right-solid.svg" alt="Try" class="ml-2 arrow">
          </button>

          <b-collapse visible id="see-more" v-html="detail.instruction" class="mt-2 ml-3">
            {{ detail.instruction }}
          </b-collapse>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { getIdFromUrl } from "vue-youtube";
import "@/plugins/vue-youtube";
import permission from "@/services/user.permission";
import _ from "lodash";

export default {
    name: "RushAcademyTutorialVideo",
    props: ["detail", "courses"],
    data() {
        return {
            fetchingCurrentTime: null
        };
    },
    computed: {
        ...mapState("auth", {
            isLogin: state => state.login
        }),
        ...mapGetters({
            errors: "rushAcademy/errors"
        }),
        videoId: {
            get() {
                return getIdFromUrl(this.detail.url);
            },
            set(id) {
                return id;
            }
        },
        lastVideoId: {
            get() {
                const storeCourses = JSON.parse(JSON.stringify(this.courses));
                return getIdFromUrl(storeCourses.pop().url);
            },
            set(id) {
                return id;
            }
        },
        redirectUrl: {
            get() {
                const code = this.detail.submodule_code;
                const transformUrl = code => {
                    const splitColon = code.includes(":")
                        ? code.split(":").slice(0, -1)
                        : code.split(":");
                    return `/${splitColon
                        .map(parts => {
                            const splitParts = parts.split(".");
                            return splitColon.length > 1
                                ? splitParts.pop()
                                : splitParts.join("/");
                        })
                        .join("/")}`;
                };
                return transformUrl(code);
            },
            set(url) {
                return url;
            }
        },
        computedInProgressStartTime: {
            get() {
                const current_time_seconds = time => {
                    return this.$moment(time, "HH:mm:ss: A").diff(
                        this.$moment().startOf("day"),
                        "seconds"
                    );
                };
                return this.detail.status < 3 && this.detail.current_time
                    ? current_time_seconds(this.detail.current_time)
                    : null;
            },
            set(time) {
                return time;
            }
        }
    },
    mounted() {
        this.$root.$on("save-video-progress", () => this.saveVideoProgress());
        this.$root.$on("play-video", time => {
            const current_time_seconds = this.$moment(time, "HH:mm:ss: A").diff(
                this.$moment().startOf("day"),
                "seconds"
            );
            if (time) this.$refs.youtube.player.seekTo(current_time_seconds);
            else this.$refs.youtube.player.playVideo();
        });
        this.$root.$on("pause-video", () =>
            this.$refs.youtube.player.pauseVideo()
        );
        this.$root.$on("router-change", toRoute =>
            this.handleRouteChanged(toRoute)
        );
    },
    beforeDestroy() {
        this.$root.$off("save-video-progress");
        this.$root.$off("play-video");
        this.$root.$off("pause-video");
        this.$root.$off("router-change");
    },
    methods: {
        ...mapActions({
            getRushAcademy: "rushAcademy/getRushAcademy",
            saveRushAcademyProgress: "rushAcademy/saveRushAcademyProgress"
        }),
        async handlePlaying(value) {
            this.$emit("handle-playing-video", value);
            if (value) {
                this.fetchingCurrentTime = setInterval(async () => {
                    this.$emit("set-playing-progress", {
                        video_id: getIdFromUrl(this.detail.url),
                        current_time: await this.$refs.youtube.player.getCurrentTime()
                    });
                }, 300);
            } else {
                clearInterval(this.fetchingCurrentTime);
                await this.saveVideoProgress();
            }
        },
        async handleVideoEnded() {
            clearInterval(this.fetchingCurrentTime);
            await this.saveVideoProgress();
            this.$emit("handle-video-ended", this.videoId);
        },
        async saveVideoProgress() {
            this.$emit("reset-play-status", getIdFromUrl(this.detail.url));
            const payload = {
                current_time: this.$moment()
                    .startOf("day")
                    .seconds(await this.$refs.youtube.player.getCurrentTime())
                    .format("HH:mm:ss"),
                video_uuid: this.detail.uuid
            };
            await this.saveRushAcademyProgress(payload);
            if (_.isEmpty(this.errors)) {
                this.getRushAcademy();
            }
        },
        async handlePlayNext(videoId) {
            await this.saveVideoProgress();
            if (_.isEmpty(this.errors)) {
                const current_video = this.courses.find(
                    video => getIdFromUrl(video.url) === videoId
                );
                const current_video_index = this.courses.indexOf(current_video);
                const next_video = this.courses[current_video_index + 1];
                this.$nextTick(() => {
                    this.$router.push({
                        path: `/rush-academy/${getIdFromUrl(next_video.url)}`
                    });
                    this.$emit("change-video", next_video);
                });
            }
        },
        hasPermissionTo(module) {
            if (!this.isLogin) return false;

            return permission.hasAccess(module);
        },
        async handleRedirectUrl() {
            clearInterval(this.fetchingCurrentTime);
            await this.saveVideoProgress();
            this.courses.map(course => (course.is_playing = false));
            if (_.isEmpty(this.errors)) {
                this.$router.push({ path: this.redirectUrl });
            }
        },
        async handleRouteChanged(toRoute) {
            clearInterval(this.fetchingCurrentTime);
            await this.saveVideoProgress();
            if (_.isEmpty(this.errors)) {
                this.$parent.isSaved = true;
                this.$router.push({ path: toRoute.path });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;700&display=swap");
* {
    font-family: MainFontRegular, sans-serif;
}
h2 {
    font-size: 22px;
    font-family: MainFontBold, sans-serif;
    font-weight: normal;
    color: #2f3941;
}
.rounded {
    border-radius: 10px !important;
}
.description {
    font-size: 14px;
    color: #68737d;
}
#play-next-button {
    font-size: 14px;
    font-family: MainFontBold, sans-serif;
    font-weight: normal;
    color: #5182ff;
}
#try-button,
#see-more-button {
    font-size: 16px;
    font-family: MainFontBold, sans-serif;
    font-weight: normal;
}

#see-more-button:not(.collapsed) >>> .arrow {
    transform: rotate(90deg);
}
.arrow {
    transition: all 0.2s 0.2s;
    transform: rotate(0);
}
</style>
